import React, { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Check } from 'lucide-react';

const PricingSection = () => {
  const sectionRef = useRef<HTMLElement>(null);

  const freedomFeatures = [
    "Unlimited candidate unlocks (fair usage)",
    "No seat limits",
    "No salary caps",
    "ATS integrations with all major platforms",
    "Email and chat support",
  ];

  return (
    <section ref={sectionRef} className="py-24 bg-brand-offwhite/50">
      <div className="container px-4 mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            One platform. 2 ways to hire.
          </h2>
          <p className="text-xl md:text-2xl font-medium text-gray-600">
            Same shortlist engine. Use it yourself with our startup plan, or let our team handle everything.
          </p>
        </div>

        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Offering: Self-serve */}
          <div className="flex flex-col">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Hire yourself</h3>
            </div>
            <div className="bg-white rounded-[32px] shadow-medium border border-gray-100 overflow-hidden flex-1 flex flex-col relative">
              <div className="p-8 md:p-10 text-center flex-1 flex flex-col">
                <div className="flex flex-col items-center mb-8">
                  <div className="bg-cs-primary/10 text-cs-primary p-3 rounded-2xl mb-4">
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Freedom</h4>
                    <p className="text-gray-500 text-sm h-10 flex items-center justify-center">For startups and small teams (1–25 employees)</p>
                  </div>
                </div>

                <div className="flex-1 flex flex-col justify-center mb-10">
                  <div className="space-y-8">
                    <div className="text-center h-24 flex flex-col justify-center">
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <span className="text-5xl font-black text-cs-primary">₹99,970</span>
                        <span className="text-lg font-bold text-cs-primary/60">annually</span>
                      </div>
                      <p className="text-sm text-gray-500 font-medium">Or ₹10,000/month</p>
                    </div>

                    <div className="flex justify-center">
                      <div className="space-y-4 inline-flex flex-col">
                        {freedomFeatures.map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                            <div className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
                              <Check className="w-3.5 h-3.5 text-emerald-600" />
                            </div>
                            <span className="text-gray-700 font-medium text-left text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="w-full">
                  <Button asChild className="w-full h-14 rounded-xl text-lg bg-cs-primary hover:bg-cs-primary/90">
                    <a href="https://cutshort.io/?auth=employer" target="_blank" rel="noopener noreferrer">
                      Start free trial
                    </a>
                  </Button>
                </div>

                {/* Social Proof */}
                <div className="mt-8 pt-8 border-t border-gray-50">
                  <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold mb-4">Used by companies like:</p>
                  <div className="flex flex-wrap justify-center items-center gap-6 opacity-80">
                    {[
                      { name: 'Appknox', src: '/lovable-uploads/appknox.png' },
                      { name: 'AiSensy', src: '/lovable-uploads/aisensy.png' },
                      { name: 'One Impression', src: '/lovable-uploads/oneimpression.png' },
                      { name: 'Virtana', src: '/lovable-uploads/virtana.png' },
                    ].map((logo) => (
                      <img key={logo.name} src={logo.src} alt={logo.name} className="h-4 md:h-5 w-auto object-contain" title={logo.name} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Offering: Managed Service */}
          <div className="flex flex-col">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Let Cutshort handle everything</h3>
            </div>
            <div className="bg-white rounded-[32px] shadow-medium border border-gray-100 overflow-hidden flex-1 flex flex-col">
              <div className="p-8 md:p-10 text-center flex-1 flex flex-col">
                <div className="flex flex-col items-center mb-8">
                  <div className="bg-purple-100 text-purple-600 p-3 rounded-2xl mb-4">
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Full recruitment support</h4>
                    <p className="text-gray-600 text-sm max-w-sm mx-auto h-10 flex items-center justify-center">
                      For busy founders who want to hire top talent, faster.
                    </p>
                  </div>
                </div>

                <div className="flex-1 flex flex-col justify-center mb-10">
                  <div className="space-y-8">
                    <div className="text-center h-24 flex flex-col justify-center">
                      <div className="flex items-center justify-center gap-3 mb-1">
                        <span className="text-2xl text-gray-400 line-through font-medium">10%</span>
                        <span className="text-5xl font-black text-cs-primary">8.33%</span>
                      </div>
                      <p className="text-sm text-gray-500 font-medium">of CTC per hire</p>
                    </div>
                    
                    <div className="flex justify-center">
                      <div className="space-y-4 inline-flex flex-col min-h-[120px]">
                        {[
                          "No retainer or upfront cost",
                          "Pay only upon joining",
                          "45 day replacement period"
                        ].map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                            <div className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
                              <Check className="w-3.5 h-3.5 text-emerald-600" />
                            </div>
                            <span className="text-gray-700 font-medium text-left text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="w-full">
                  <Button asChild className="w-full h-14 rounded-xl text-lg bg-cs-primary hover:bg-cs-primary/90">
                    <a href="https://share.hsforms.com/1hCH9XA96TFOoIblaesPhvgbpcum" target="_blank" rel="noopener noreferrer">
                      Get a callback
                    </a>
                  </Button>
                </div>

                {/* Social Proof */}
                <div className="mt-8 pt-8 border-t border-gray-50">
                  <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold mb-4">Used by companies like:</p>
                  <div className="flex flex-wrap justify-center items-center gap-6 opacity-80">
                    {[
                      { name: 'Shoppin', src: '/lovable-uploads/shoppin.png' },
                      { name: 'Shiprocket', src: '/lovable-uploads/Shiprocket.png' },
                      { name: 'Averlon', src: '/lovable-uploads/Averlon.png' },
                      { name: 'Step Security', src: '/lovable-uploads/StepSecurity.png' },
                    ].map((logo) => (
                      <img key={logo.name} src={logo.src} alt={logo.name} className="h-4 md:h-5 w-auto object-contain" title={logo.name} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
